import { Dispatch } from 'redux';
import GoogleController from '../controllers/GoogleController';
import commonHandler from '../errorHandlers/common-handler';
import { googleSheetsHandler } from '../errorHandlers/google-sheets-handler';
import { GoogleAccount, OauthUrlResponse } from '../interfaces/googleSheet';
import { activeFacebookPageIdSelector } from '../selectors/viewSelectors';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import connectionsCRUDActions from './ConnectionsCRUDActions';
import CrudActionContainer from './CrudActionContainer';

class GoogleSheetsCRUDActions extends CrudActionContainer {
  retrieveAccounts: string;
  retrieveSpreadsheets: string;
  retrieveSheets: string;
  retrieveColumns: string;
  revokeAccess: string;

  constructor() {
    super('GOOGLE_SHEETS');
    this.retrieveAccounts = this.makeActionKey('RETRIEVED_ACCOUNTS');
    this.retrieveSpreadsheets = this.makeActionKey('RETRIEVED_SPREADSHEETS');
    this.retrieveSheets = this.makeActionKey('RETRIEVED_SHEETS');
    this.retrieveColumns = this.makeActionKey('RETRIEVED_COLUMNS');
    this.revokeAccess = this.makeActionKey('REVOKE_ACCESS');
  }

  getOauthUrl(redirectTo: string = 'post_message') {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const state = getState();
      const facebookPageId = activeFacebookPageIdSelector(state);
      try {
        const response: OauthUrlResponse = await GoogleController.getOauthUrl(
          redirectTo,
          'sheets',
          facebookPageId,
        );

        return response.link;
      } catch (error) {
        commonHandler(dispatch)(error);
      } finally {
        this.unbusy();
      }
    };
  }

  getAccessToken(
    facebookPageId: number,
    accountId: number,
    throwErrors: boolean = false,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await GoogleController.getAccessToken(
          facebookPageId,
          accountId,
        );
        return response.accessToken;
      } catch (error) {
        if (throwErrors) {
          throw error;
        }
        commonHandler(dispatch)(error);
      }
    };
  }

  getAccounts(
    facebookPageId: number,
    options?: { sortByUpdatedAtDesc?: boolean },
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<GoogleAccount[] | undefined> => {
      dispatch(this.busy(this.retrieveAccounts));
      try {
        const response = (await GoogleController.getAccounts(
          facebookPageId,
        )) as GoogleAccount[];
        dispatch({
          type: this.retrieveAccounts,
          accounts: response,
        });
        if (options?.sortByUpdatedAtDesc) {
          return response.sort(
            (a, b) => b.updatedAt?.localeCompare(a.updatedAt || '') || 0,
          );
        }
        return response;
      } catch (error) {
        googleSheetsHandler(dispatch)({ error });
      }
    };
  }

  getSpreadsheets(accountId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveSpreadsheets));
      try {
        const response = await GoogleController.getSpreadsheets(accountId);
        dispatch({
          type: this.retrieveSpreadsheets,
          spreadsheets: response,
        });
        return response;
      } catch (error) {
        googleSheetsHandler(dispatch)({ error });
      }
    };
  }

  getSheets(accountId: number, spreadsheetId: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveSheets));
      try {
        const response = await GoogleController.getSheets(
          accountId,
          spreadsheetId,
        );

        dispatch({
          type: this.retrieveSheets,
          sheets: response,
        });
        return response;
      } catch (error) {
        googleSheetsHandler(dispatch)({ error });
      }
    };
  }
  getColumns(accountId: number, spreadsheetId: string, sheetTitle: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveColumns));
      try {
        const response = await GoogleController.getColumns(
          accountId,
          spreadsheetId,
          sheetTitle,
        );

        dispatch({
          type: this.retrieveColumns,
          columns: response,
        });
        return response;
      } catch (error) {
        googleSheetsHandler(dispatch)({ error });
      }
    };
  }

  disconnectAccount(accountId: number, fbPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.revokeAccess));
      try {
        const response = await GoogleController.revokeAccess(accountId);

        dispatch({
          type: this.revokeAccess,
        });
        dispatch(this.getAccounts(fbPageId));
        dispatch(
          connectionsCRUDActions.retrieveAll(fbPageId, {
            type: 'google_spreadsheets',
            pageSize: 9999,
          }),
        );
        return response;
      } catch (error) {
        googleSheetsHandler(dispatch)({ error });
      }
    };
  }
}

const googleSheetsCRUDActions = new GoogleSheetsCRUDActions();

export default googleSheetsCRUDActions;

export const useGoogleSheetsCRUDActions = getActionHookBuilder(
  googleSheetsCRUDActions,
  [
    'getOauthUrl',
    'getAccessToken',
    'getAccounts',
    'getSpreadsheets',
    'getSheets',
    'getColumns',
    'disconnectAccount',
  ],
);
